<template>
    <div
        class="bundle-template-line-item"
        :class="`bundle-template-line-item-${option.inputType}`"
    >
        <DebugContent>
            <details>
                <summary class="cursor-pointer">DEBUG: Item</summary>
                <ul class="font-size-7">
                    <li>
                        <div>ATTR UID:</div>
                        <strong>{{ attrUid }}</strong>
                    </li>
                    <li>
                        <div>MODEL:</div>
                        <strong>{{ modelValue }}</strong>
                    </li>
                    <li>
                        <details>
                            <summary class="cursor-pointer">OPTIONS:</summary>
                            <ul>
                                <li
                                    v-for="(opt, i) in option.values"
                                    :key="`btl-opt-${i}`"
                                >
                                    <dl>
                                        <dt>{{ opt.name }}</dt>
                                        <dd>{{ opt.uid }}</dd>
                                    </dl>
                                </li>
                            </ul>
                        </details>
                    </li>
                </ul>
            </details>
        </DebugContent>

        <InlineColorSelectorInput
            v-if="option.inputType === 'color'"
            :model-value="model!"
            :colors="option.values"
            :disabled-items="optionsDisabledModel"
            @update:model-value="onChange"
            mandatory
            hide-selected-label
        ></InlineColorSelectorInput>
        <v-select
            v-else-if="option.inputType === 'dropdown'"
            :model-value="model!"
            :items="option.values"
            item-value="uid"
            density="compact"
            hide-details="auto"
            @update:model-value="onChange"
        >
            <template #selection="{ internalItem }">
                <div
                    class="d-flex align-center"
                    :class="{
                        'opacity-70': optionsDisabledModel?.has(internalItem.raw.uid)
                    }"
                >
                    {{ internalItem.raw.name }}
                    <v-chip
                        v-if="optionsDisabledModel?.has(internalItem.raw.uid)"
                        class="ml-2"
                        variant="outlined"
                        size="small"
                        density="compact"
                        color="error"
                        slim
                        label
                    >
                        not available
                    </v-chip>
                </div>
            </template>
            <template #item="{ internalItem, props }">
                <v-list-item
                    v-bind="props"
                    title=""
                    :class="{
                        'opacity-70': optionsDisabledModel?.has(internalItem.raw.uid)
                    }"
                    :disabled="optionsDisabledModel?.has(internalItem.raw.uid)"
                >
                    <span>{{ internalItem.raw.name }}</span>
                    <span
                        v-if="internalItem.raw.description"
                        class="font-size-8"
                    >
                        ({{ internalItem.raw.description }})
                    </span>
                    <v-chip
                        v-if="optionsDisabledModel?.has(internalItem.raw.uid)"
                        class="ml-2"
                        variant="outlined"
                        size="small"
                        density="compact"
                        color="error"
                        slim
                        label
                    >
                        not available
                    </v-chip>
                </v-list-item>
            </template>
        </v-select>
        <v-item-group
            v-else-if="option.inputType === 'button'"
            :model-value="model!"
            class="option-btn-group-v2"
            :class="btnColClass"
            @update:model-value="onChange($event)"
        >
            <v-item
                v-for="(opt, index) in option.values"
                :key="`item-${attrUid}-${opt.uid}`"
                :value="opt.uid"
                @update:model-value="onChange"
                #default="{ isSelected, toggle, value }"
            >
                <v-btn
                    :key="`option-${index}`"
                    variant="outlined"
                    class="rounded"
                    :class="{
                        'selected': ((value as any) === model!),
                        'option-off': optionsDisabledModel?.has(opt.uid)
                    }"
                    @click="toggle"
                >
                    <strong>{{ opt.name }}</strong>
                </v-btn>
            </v-item>
        </v-item-group>
    </div>
</template>

<script lang="ts" setup>
    import InlineColorSelectorInput from '~/components/controls/color/InlineColorSelectorInput/InlineColorSelectorInput.vue';
    import DebugContent from '~/components/DebugContent/DebugContent.vue';

    import type {
        BundleTemplateLineOptionsData,
        GenericObject
    } from '~/types';

    const props = defineProps<{
        modelValue: GenericObject<Nullable<string>>;
        option: BundleTemplateLineOptionsData;
        optionsDisabledModel?: Set<string>;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: string];
    }>();

    const attrUid = computed<string>(() => props.option.attributeUid);
    const model = computed(() => props.modelValue[attrUid.value]);
    const btnColClass = computed<string>(() => {
        if (props.option.inputType !== 'button') return '';
        const len = props.option.values.length;
        return len < 4 ? `g-col-${len}` : 'g-col-2';
    });

    function onChange(model: Nullable<string>): void {
        if (model) {
            emit('update:modelValue', model);
        }
    }
</script>

<style lang="scss"></style>
